/**
 * OutOfScopeFilterAdjustPrompt.tsx
 *
 * Inline AI message card rendered when the AI detects a user request needs
 * data outside the dashboard's current filter scope (mockup 19).
 *
 * Brand guide §28 — Out-of-scope response pattern.
 *
 * Acceptance behaviour:
 *  - "Add [requested] to this dashboard" → invokes the `onExpandScope` callback
 *    with the current `prompt`. The parent is the single owner of the Redux
 *    transition and any downstream sync — this component does NOT dispatch
 *    `expandScope` itself (avoids double-firing the state transition).
 *  - "Keep as is" → dispatches `cancelScopeExpansion` (clears the prompt,
 *    no filter change). This action is fully local to the component because
 *    cancellation has no parent-owned side effects.
 *
 * NOTE: the secondary-line copy under the CTA mentions that accepting will
 * "sync and rebuild your panels". In v1 the real sync/rebuild still lives in
 * the v1.01 backend work (see Canvas.handleExpandScope JSDoc + the v1 TDD
 * §3d.5). The copy is kept as-is because the parent owns the sync contract —
 * this component only surfaces the user's intent.
 *
 * Accessibility:
 *  - role="note" aria-label="Data scope notice" on the scope callout.
 *  - CTA buttons have descriptive aria-labels.
 *  - focus-visible:ring-2 focus-visible:ring-focusRing on all interactive elements.
 */

import React from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { Button } from '@/components/ui/button'
import type { AppDispatch } from '@/redux'
import { dashboardViewActions } from '@/redux/slice/dashboardViewSlice'
import type { OutOfScopePrompt } from '@/types/DashboardV1.types'

interface OutOfScopeFilterAdjustPromptProps {
  prompt: OutOfScopePrompt
  /**
   * Called when the user clicks "Add [requested] to this dashboard".
   * The parent is the single owner of the Redux transition (dispatches
   * `expandScope` + any backend sync). This component intentionally does
   * not dispatch `expandScope` itself — doing so would double-fire the
   * state transition.
   */
  onExpandScope: (prompt: OutOfScopePrompt) => void
}

/**
 * Formats a scope value (string or string[]) for display.
 */
function formatScope(value: string | string[]): string {
  if (Array.isArray(value)) return value.join(', ')
  return value
}

export const OutOfScopeFilterAdjustPrompt: React.FC<OutOfScopeFilterAdjustPromptProps> = ({
  prompt,
  onExpandScope,
}) => {
  const dispatch: AppDispatch = useDispatch()

  const requestedLabel = formatScope(prompt.requested)
  const currentScopeLabel = formatScope(prompt.currentScope)

  // Parent owns the Redux transition for expand — see prop JSDoc. We only
  // surface the user's intent; we do not dispatch `expandScope` here.
  const handleExpand = () => {
    onExpandScope(prompt)
  }

  const handleCancel = () => {
    dispatch(dashboardViewActions.cancelScopeExpansion())
  }

  return (
    <div className="text-sm text-primary leading-relaxed">
      <p>
        I can see your dashboard is currently scoped to{' '}
        <strong>{currentScopeLabel}</strong>. I don&apos;t have{' '}
        {requestedLabel} data loaded for this dashboard.
      </p>

      {/* Scope callout — amber warning treatment per brand guide §28 */}
      <div
        className="mt-2.5 p-3 bg-warning-foreground border border-warning/30 rounded-xl"
        role="note"
        aria-label="Data scope notice"
      >
        <div className="flex items-center gap-1.5 mb-1.5">
          <AlertTriangle size={12} className="text-warning flex-shrink-0" aria-hidden="true" />
          <p className="text-xs font-bold text-warning">Outside your current scope</p>
        </div>
        <p className="text-xs text-warning/80">
          This dashboard was set up for {currentScopeLabel}.{' '}
          {requestedLabel} data isn&apos;t included in your current filters.
        </p>
      </div>

      {/* Recovery CTA */}
      <Button
        variant="outline"
        className="mt-3 w-full flex items-center justify-center gap-1.5 border-2 border-accent text-accent hover:bg-accent-background text-sm font-semibold focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
        onClick={handleExpand}
        aria-label={`Add ${requestedLabel} to this dashboard`}
      >
        <Plus size={12} aria-hidden="true" />
        Add {requestedLabel} to this dashboard
      </Button>

      {/* Secondary line: describes what "Add to scope" does at the dashboard
          level. We deliberately do not promise immediate sync/rebuild here —
          in v1 this action queues the scope expansion; the full data pull
          ships with the v1.01 filter-expansion API (see Canvas handler). */}
      <p className="mt-1.5 text-xs text-muted-foreground">
        We&apos;ll include {requestedLabel} in your dashboard scope.
      </p>

      {/* Keep as is — secondary action. Uses Button primitive for consistent
          focus-visible; override variant styling to match brand guide §28
          (muted text with underline, no background). */}
      <Button
        type="button"
        variant="link"
        className="mt-2 h-auto p-0 text-xs text-muted-foreground underline underline-offset-2 hover:text-secondary rounded-sm"
        onClick={handleCancel}
        aria-label="Keep current filters and cancel this request"
      >
        Keep as is
      </Button>
    </div>
  )
}
